import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Box, Group, Button, Paper } from '@mantine/core';
import { useEffect } from 'react';
import { 
    AiOutlineBold, 
    AiOutlineItalic, 
    AiOutlineUnorderedList, 
    AiOutlineOrderedList,
    AiOutlineLink
} from 'react-icons/ai';
import './RichTextEditor.css';

interface RichTextEditorProps {
    value: string;
    onChange: (html: string) => void;
    placeholder?: string;
}

export const RichTextEditor = ({ value, onChange }: RichTextEditorProps) => {
    const editor = useEditor({
        extensions: [
            StarterKit,
            Link.configure({
                openOnClick: false,
            }),
        ],
        content: value,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
    });

    // FNP-1447: this useEffect used to sit AFTER the `if (!editor) return null`
    // early return, so on the first render — when useEditor has not resolved yet —
    // React saw one fewer hook than on every later render. That is
    // react-hooks/rules-of-hooks, and it corrupts the hook order rather than merely
    // being untidy: state can attach to the wrong slot once the editor appears.
    //
    // The guard is inside the effect instead, which is where it belonged: the effect
    // must be registered unconditionally, and it simply does nothing until there is
    // an editor to sync.
    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value);
        }
    }, [value, editor]);

    if (!editor) {
        return null;
    }

    const addLink = () => {
        const url = window.prompt('Enter URL:');
        if (url) {
            editor.chain().focus().setLink({ href: url }).run();
        }
    };

    return (
        <Paper withBorder>
            <Group p="xs" style={{ borderBottom: '1px solid #dee2e6' }}>
                <Button
                    size="xs"
                    variant={editor.isActive('bold') ? 'filled' : 'subtle'}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                >
                    <AiOutlineBold />
                </Button>
                <Button
                    size="xs"
                    variant={editor.isActive('italic') ? 'filled' : 'subtle'}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                >
                    <AiOutlineItalic />
                </Button>
                <Button
                    size="xs"
                    variant={editor.isActive('bulletList') ? 'filled' : 'subtle'}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                >
                    <AiOutlineUnorderedList />
                </Button>
                <Button
                    size="xs"
                    variant={editor.isActive('orderedList') ? 'filled' : 'subtle'}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                >
                    <AiOutlineOrderedList />
                </Button>
                <Button
                    size="xs"
                    variant={editor.isActive('link') ? 'filled' : 'subtle'}
                    onClick={addLink}
                >
                    <AiOutlineLink />
                </Button>
            </Group>
            <Box p="md" style={{ minHeight: '200px' }}>
                <EditorContent editor={editor} />
            </Box>
        </Paper>
    );
};
